const DEFAULT_RUM_ENDPOINT = 'https://dataplane.rum.us-east-1.amazonaws.com';
const DEFAULT_RUM_REGION = 'us-east-1';
const DEFAULT_RUM_APPLICATION_VERSION = '1.1.6';
const DEFAULT_RUM_CLIENT_URL = 'https://client.rum.us-east-1.amazonaws.com/3.x/cwr.js';
const DEFAULT_RUM_NAMESPACE = 'cwr';

let initialized = false;
let scriptLoadPromise = null;

const isBrowser = () => (
    typeof window !== 'undefined' && typeof document !== 'undefined'
);

const isEnabled = (value) => (
    String(value || '').trim().toLowerCase() === 'true'
);

export const getAwsRumRuntimeConfig = () => ({
    enabled: isEnabled(process.env.REACT_APP_AWS_RUM_ENABLED),
    applicationId: String(process.env.REACT_APP_AWS_RUM_APPLICATION_ID || '').trim(),
    applicationVersion: String(
        process.env.REACT_APP_AWS_RUM_APPLICATION_VERSION || DEFAULT_RUM_APPLICATION_VERSION
    ).trim(),
    region: String(process.env.REACT_APP_AWS_RUM_REGION || DEFAULT_RUM_REGION).trim(),
    namespace: String(process.env.REACT_APP_AWS_RUM_NAMESPACE || DEFAULT_RUM_NAMESPACE).trim(),
    clientUrl: String(process.env.REACT_APP_AWS_RUM_CLIENT_URL || DEFAULT_RUM_CLIENT_URL).trim(),
});

export const buildAwsRumConfig = () => ({
    sessionSampleRate: 1,
    endpoint: DEFAULT_RUM_ENDPOINT,
    telemetries: ['performance', 'errors', 'http'],
    allowCookies: true,
    enableXRay: false,
    signing: false,
    recordResourceUrl: false,
});

const loadRumScript = (clientUrl) => {
    if (scriptLoadPromise) return scriptLoadPromise;

    scriptLoadPromise = new Promise((resolve, reject) => {
        const existingScript = document.querySelector(`script[data-aws-rum-client="${clientUrl}"]`);
        if (existingScript) {
            existingScript.addEventListener('load', resolve, { once: true });
            existingScript.addEventListener('error', reject, { once: true });
            return;
        }

        const script = document.createElement('script');
        script.async = true;
        script.src = clientUrl;
        script.dataset.awsRumClient = clientUrl;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    }).catch(() => {
        scriptLoadPromise = null;
        return null;
    });

    return scriptLoadPromise;
};

export const initializeAwsRum = () => {
    const runtimeConfig = getAwsRumRuntimeConfig();
    if (!isBrowser() || !runtimeConfig.enabled || !runtimeConfig.applicationId) {
        return null;
    }

    if (initialized) {
        return window.AwsRumClient || null;
    }

    const clientState = {
        n: runtimeConfig.namespace,
        i: runtimeConfig.applicationId,
        v: runtimeConfig.applicationVersion,
        r: runtimeConfig.region,
        c: buildAwsRumConfig(),
        q: [],
    };

    window.AwsRumClient = clientState;
    window[runtimeConfig.namespace] = (command, payload) => {
        clientState.q.push({ c: command, p: payload });
    };

    initialized = true;
    loadRumScript(runtimeConfig.clientUrl);
    return clientState;
};

export const recordRumEvent = (eventName, data = {}) => {
    const runtimeConfig = getAwsRumRuntimeConfig();
    if (!isBrowser() || !runtimeConfig.enabled || !runtimeConfig.applicationId || !eventName) {
        return false;
    }

    if (!initialized) {
        initializeAwsRum();
    }

    const payload = {
        type: eventName,
        data,
    };
    const rumCommand = window[runtimeConfig.namespace];
    if (typeof rumCommand === 'function') {
        rumCommand('recordEvent', payload);
        return true;
    }

    if (window.AwsRumClient?.q) {
        window.AwsRumClient.q.push({ c: 'recordEvent', p: payload });
        return true;
    }

    return false;
};

export const __resetAwsRumForTests = () => {
    initialized = false;
    scriptLoadPromise = null;
    if (isBrowser()) {
        delete window.AwsRumClient;
        delete window[DEFAULT_RUM_NAMESPACE];
    }
};
